import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react"
import { useDispatch } from "react-redux"
import { useMatch, useNavigate } from "react-router-dom"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useKellyAnalytics } from "../../chatbox/use-kelly-analytics"
import { useLatest } from "../../chatbox/use-latest"
import { useAccountAlias } from "resources/account"
import { seedComposer } from "../../store/agent-chat-slice"
import { NudgePopover } from "./nudge-popover"
import type { NudgePrompt } from "./types"

// Persisted per account: once the user has reached Kelly, the nudge retires for good. Deliberately
// not the legacy drawer's key — users who used the legacy chat should still be nudged toward Kelly once.
const HAS_USED_KELLY_CHAT_KEY = "kelly-ai-has-used-chat"
// Same-tab change signal for useSyncExternalStore ("storage" events only fire cross-tab).
const HAS_USED_CHANGE_EVENT = "kelly-ai-has-used-chat-change"

const KELLY_ROUTE_PATTERN = "/:account/kelly-ai/*"

type RetireReason = "opened_chat" | "dismissed" | "question_clicked"

function readHasUsedKellyChat(accountAlias: string): boolean {
  try {
    return window.localStorage.getItem(`${HAS_USED_KELLY_CHAT_KEY}-${accountAlias}`) === "1"
  } catch {
    // Storage unavailable — default to "not used" so discoverability still wins.
    return false
  }
}

// Server snapshot for useSyncExternalStore — matches the unavailable-storage fallback above.
function readHasUsedNoStorageSnapshot(): boolean {
  return false
}

function persistHasUsedKellyChat(accountAlias: string): void {
  try {
    window.localStorage.setItem(`${HAS_USED_KELLY_CHAT_KEY}-${accountAlias}`, "1")
  } catch {
    // Ignore — storage unavailable.
  }
  window.dispatchEvent(new Event(HAS_USED_CHANGE_EVENT))
}

function subscribeToHasUsedChange(onChange: () => void): () => void {
  window.addEventListener(HAS_USED_CHANGE_EVENT, onChange)
  return () => window.removeEventListener(HAS_USED_CHANGE_EVENT, onChange)
}

interface KellyNudgeProps {
  /** The nav item's DOM root (null until it mounts); the caller supplies it via a callback ref. */
  anchorEl: HTMLElement | null
}

/**
 * Discoverability nudge for the Kelly AI nav entry. Shows until the user reaches Kelly once
 * (persisted per account) or dismisses it (this session). A clicked prompt is stored in the
 * agentChat slice and applied to the composer when the kelly-ai route mounts.
 */
export function KellyNudge({ anchorEl }: KellyNudgeProps) {
  const accountAlias = useAccountAlias()
  // Kelly's analytics, not the ambient page context: the nav rail sits under no analytics provider, so
  // `useAnalytics()` gave these three events no `contextPrefixes` at all and Mixpanel's Kelly reports —
  // which filter on that array — excluded the whole nudge funnel.
  const analytics = useKellyAnalytics()
  // Read through a ref in the shown effect below: the surface properties carry `route` and `dockView`, so
  // `analytics` gets a new identity on every in-account navigation and dock toggle.
  const analyticsRef = useLatest(analytics)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isOnKellyRoute = useMatch(KELLY_ROUTE_PATTERN) != null
  // Session-only, keyed by account — the nav rail survives SELECT_ACCOUNT, so a plain boolean would
  // leak one account's dismissal into the next. The persisted has-used flag retires across sessions.
  const [dismissedAliases, setDismissedAliases] = useState<ReadonlySet<string>>(new Set())
  const dismissed = dismissedAliases.has(accountAlias)
  // Multiple retire paths can fire together (mousedown + click-away); track only the first per account.
  const dismissTrackedAliasesRef = useRef(new Set<string>())

  const readHasUsedSnapshot = useCallback(() => readHasUsedKellyChat(accountAlias), [accountAlias])
  const hasUsedChat = useSyncExternalStore(subscribeToHasUsedChange, readHasUsedSnapshot, readHasUsedNoStorageSnapshot)

  // Reaching Kelly by any path (deep link, redirect) counts as having used it.
  useEffect(() => {
    if (isOnKellyRoute && !hasUsedChat && accountAlias) persistHasUsedKellyChat(accountAlias)
  }, [isOnKellyRoute, hasUsedChat, accountAlias])

  const showNudge = !!accountAlias && !hasUsedChat && !dismissed && !isOnKellyRoute && !!anchorEl

  // Keyed on visibility alone — `analytics` would re-fire the event on every navigation.
  useEffect(() => {
    if (showNudge) analyticsRef.current.track(AI_TEAMMATE_EVENTS.NUDGE_SHOWN)
  }, [showNudge, analyticsRef])

  const retireNudge = useCallback((reason: RetireReason) => {
    setDismissedAliases((previous) => new Set(previous).add(accountAlias))
    if (!dismissTrackedAliasesRef.current.has(accountAlias)) {
      dismissTrackedAliasesRef.current.add(accountAlias)
      analyticsRef.current.track(AI_TEAMMATE_EVENTS.NUDGE_DISMISSED, { reason })
    }
    // Via the ref: `analytics` re-identifies on every navigation, and the mousedown effect depends on this.
  }, [analyticsRef, accountAlias])

  // Native listener on the anchor: mousedown fires before NudgePopover's ClickAwayListener, so a
  // nav-item click retires the nudge with its true reason instead of a generic click-away dismissal.
  useEffect(() => {
    if (!anchorEl || !showNudge) return undefined
    const handleAnchorMouseDown = () => {
      persistHasUsedKellyChat(accountAlias)
      retireNudge("opened_chat")
    }
    anchorEl.addEventListener("mousedown", handleAnchorMouseDown)
    return () => anchorEl.removeEventListener("mousedown", handleAnchorMouseDown)
  }, [anchorEl, showNudge, accountAlias, retireNudge])

  const handleClose = useCallback(() => {
    retireNudge("dismissed")
  }, [retireNudge])

  const handleQuestionClick = useCallback((prompt: NudgePrompt) => {
    persistHasUsedKellyChat(accountAlias)
    analyticsRef.current.track(AI_TEAMMATE_EVENTS.NUDGE_QUESTION_CLICKED, { prompt_id: prompt.id })
    retireNudge("question_clicked")
    dispatch(seedComposer(prompt.text))
    navigate(`/${accountAlias}/kelly-ai`)
  }, [accountAlias, analyticsRef, retireNudge, dispatch, navigate])

  return (
    <NudgePopover
      open={showNudge}
      anchorEl={anchorEl}
      onClose={handleClose}
      onQuestionClick={handleQuestionClick}
    />
  )
}
